import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ModelId, Paginated } from '@/shared/types/common.types';
import { projectService } from '../services/projectService';
import type { ProjectDetail, ProjectQuery, ProjectSheet, ProjectSummary } from '../types/project.types';

export const projectKeys = {
  all: ['projects'] as const,
  list: (query: ProjectQuery) => ['projects', 'list', query] as const,
  detail: (id: ModelId) => ['projects', 'detail', id] as const,
  sheets: (id: ModelId) => ['projects', 'sheets', id] as const,
  allModels: ['projects', 'all'] as const,
};

export function useProjectsQuery(query: ProjectQuery): UseQueryResult<Paginated<ProjectSummary>> {
  return useQuery({
    queryKey: projectKeys.list(query),
    queryFn: () => projectService.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useProjectSheetsQuery(modelId: ModelId): UseQueryResult<readonly ProjectSheet[]> {
  return useQuery({
    queryKey: projectKeys.sheets(modelId),
    queryFn: () => projectService.listSheets(modelId),
  });
}

export function useProjectQuery(modelId: ModelId | null): UseQueryResult<ProjectDetail> {
  return useQuery({
    queryKey: projectKeys.detail(modelId ?? (0 as ModelId)),
    queryFn: () => {
      if (modelId === null) throw new Error('A model id is required.');
      return projectService.getById(modelId);
    },
    enabled: modelId !== null,
  });
}

/**
 * Every model the user can reach, unpaged.
 *
 * Shared by the snapshot version picker and the audit run dialog, so both offer the same
 * list and it is fetched once per session rather than once per consumer. Unpaged is not a
 * shortcut here: `RevitFileProject/GetAll` takes no page parameters, so the whole list is
 * what the server sends whatever the client asks for.
 */
export function useAllModelsQuery(): UseQueryResult<readonly ProjectSummary[]> {
  return useQuery({
    queryKey: projectKeys.allModels,
    queryFn: () => projectService.listAll(),
    staleTime: 2 * 60_000,
  });
}
